import React, {useEffect, useState, useRef} from 'react';
import {ChevronDown, Eye, Plus, Upload, X} from 'lucide-react';
import {useNavVisibility} from "../NavVisibilityContext/NavVisibilityContext";
import {useClientData} from '../../contexts/ClientDataContext';
import useApi from "../../utils/useApi";
import LoadingScreen from "../LoadingScreen/LoadingScreen";
import Swal from 'sweetalert2';
import DocumentsNeededList from "../DocumentsNeededList/DocumentsNeededList";
import {Link, useParams, useSearchParams} from "react-router-dom";
import {useNavCount} from "../../contexts/NavCountProvider";
import SelectTaxYear from "../Admin/SelectTaxYear/SelectTaxYear";
import ClientActions from "../Admin/ClientActions/ClientActions";
import {IoDocuments} from "react-icons/io5";
import {useTaxYear} from "../../contexts/TaxYearContext";
import CustomerAccountActions from "../Client/CustomerAccountActions/CustomerAccountActions";

const UploadDocument = ({
                            user,
                            handleShowSettingsHeader,
                            handleShowHeader,
                            onSubmit,
                        }) => {

    const { id } = useParams();
    const { taxYear, updateTaxYear } = useTaxYear();
    const { refreshCounts } = useNavCount();
    const { setIsNavVisible } = useNavVisibility();
    const [searchParams, setSearchParams] = useSearchParams();
    const { clientData, setClientData } = useClientData();
    const { getClient, getClientHome, getDocumentStatus, uploadDocument } = useApi();
    const fileInputRef = useRef(null); // Add ref for file input
    const [availableYears, setAvailableYears] = useState([]);
    const [client, setClient] = useState(null);
    const [currentDocument, setCurrentDocument] = useState(null);
    const [documentStatus, setDocumentStatus] = useState(null);
    const [files, setFiles] = useState([]);
    const [fileNames, setFileNames] = useState({});
    const [comments, setComments] = useState('');
    const [errors, setErrors] = useState([]);
    const [showUploader, setShowUploader] = useState(false);
    const [isMenuExpanded, setIsMenuExpanded] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(false);

    // Get these values from clientData with fallbacks
    const documentsNeeded = clientData?.documentsNeeded || [];
    const documentsCount = clientData?.documentsCount || 0;
    const finishedCount = clientData?.finishedCount || 0;

    const resetForm = () => {
        setFiles([]);
        setFileNames({});
        setComments('');
        setErrors([]);
        // Reset file input value
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }

        setShowUploader(false);
    };

    useEffect(() => {
        initializeComponent();
    }, [taxYear]);

    const initializeComponent = async () => {
        handleShowSettingsHeader(false);
        handleShowHeader(true);
        setIsNavVisible(true);

        try {
            setIsLoading(true);
            console.log('Fetching client data...');
            let data = {
                year: taxYear,
            }

            if (id && user.user_type === 'admin') {
                const userResponse = await getClient(id, data);
                setClient(userResponse.client);
                setClientData(userResponse.client);
                console.log('set available years');
                console.log(userResponse.availableYears);

            } else {
                const newClientData = await getClientHome(user.id, data);
                setClientData(newClientData);
                setClient(user);
            }

            initializeDocumentStatus();
        }
        catch (error) {
            console.error('Error fetching client data:', error);
            setErrors(prev => [...prev, 'Failed to load client data. Please refresh the page.']);
        }
        finally {
            setIsLoading(false);
        }
    };

    const handleUpdateClientData = async () => {

        console.log('Fetching client data...');
        let uid = user.user_type === 'admin' ? id : user.id;
        let data = {
            year: taxYear,
        }
        const newClientData = await getClientHome(uid, data);
        setClientData(newClientData);
        setIsLoading(false);
    }

    const initializeDocumentStatus = async () => {
        let response;
        console.log(id);
        let data = {
            year: taxYear
        }
        if (id) {
            response = await getDocumentStatus(id, data);
        } else {
            response = await getDocumentStatus(user.id, data);
        }

        setAvailableYears(response.availableYears);
        setDocumentStatus(response);
        setCurrentDocument(null);
        //setCurrentDocument(response.currentDocument);
    }

    const getDocument = async (docId) => {
        console.log(docId)
        const data = {
            id: docId
        }
        console.log(data)
        let response;

        if (id) {
            response = await getDocumentStatus(id, data);
        } else {
            response = await getDocumentStatus(user.id, data);
        }

        setDocumentStatus(response);
        setCurrentDocument(response.currentDocument);
        setIsMenuExpanded(true);
        setShowUploader(true);
    }

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        setFiles(selectedFiles);

        const initialNames = {};
        selectedFiles.forEach((file, index) => {
            initialNames[index] = '';
        });
        setFileNames(initialNames);
    };

    const handleFileNameChange = (index, name) => {
        setFileNames(prev => ({
            ...prev,
            [index]: name
        }));
    };

    const validateForm = () => {
        const newErrors = [];
        const unnamedFiles = Object.values(fileNames).filter(name => !name.trim()).length;

        if (unnamedFiles > 0) {
            newErrors.push(`Please rename all of your files before uploading. ${unnamedFiles} files need to be renamed.`);
        }

        if (files.length === 0) {
            newErrors.push('Please select at least one file to upload.');
        }

        setErrors(newErrors);
        return newErrors.length === 0;
    };

    const handleDifferentDocumentUpload = () => {
        setCurrentDocument(null);
        setIsMenuExpanded(true);
        setShowUploader(true);
    }

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            setIsUploading(true);
            const formData = new FormData();
            formData.append('user_id', client.id);
            formData.append('document_comments', comments);
            formData.append('year', taxYear);

            if (currentDocument) {
                formData.append('document_id', currentDocument.id);
                formData.append('document_name', currentDocument.name);
            }

            if (user.user_type === 'admin') {
                const lockedCheckbox = document.getElementById('locked');
                formData.append('locked', lockedCheckbox?.checked ? 1 : 0);
            }

            files.forEach((file, index) => {
                formData.append('documents[]', file);
                formData.append('fileName[]', fileNames[index] || file.name);
            });

            const results = await uploadDocument(formData);

            if (results.updateClientData) {
                await handleUpdateClientData();
            }

            setIsUploading(false);
            // Show success message
            await Swal.fire({
                                title: 'Success!',
                                text: 'Documents have been successfully uploaded',
                                icon: 'success',
                                confirmButtonColor: '#3B82F6',
                                confirmButtonText: 'OK'
                            });

            // Reset form after successful upload
            resetForm();
            refreshCounts();

        }
        catch (error) {
            console.error('Upload error:', error);
            Swal.fire({
                          title: 'Error',
                          text: 'Failed to upload documents. Please try again.',
                          icon: 'error',
                          confirmButtonColor: '#3B82F6',
                          confirmButtonText: 'OK'
                      });
        }
    };

    // Show loading state while fetching data
    if (isLoading) {
        return <LoadingScreen/>;
    }

    const DocumentMenu = () => (
        <div className="w-full md:w-1/3 px-1 z-10 relative">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
                <button
                    onClick={() => setIsMenuExpanded(!isMenuExpanded)}
                    className="w-full flex justify-between items-center mb-2 text-left focus:outline-none"
                >
                    <span className={`font-semibold  ${finishedCount === documentsCount ? 'text-green-400' : 'text-gray-800 dark:text-gray-200'}`}>Documents {finishedCount === documentsCount ? 'Complete' : 'Needed'}</span>
                    <div className="flex items-center space-x-2">
                        <span className={`text-sm  ${finishedCount === documentsCount ? 'text-green-400' : 'text-gray-600 dark:text-gray-400'}`}>{finishedCount} / {documentsCount}</span>
                        <ChevronDown
                            className={`w-4 h-4 transform transition-transform duration-200 ${isMenuExpanded ? 'rotate-180' : ''}`}
                        />
                    </div>
                </button>

                {!isMenuExpanded && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Expand to see progress</p>
                )}

                <p className="text-sm font-bold text-gray-500 dark:text-gray-400 mb-4">Click a document to start uploading.</p>

                {isMenuExpanded && (
                    <div className="space-y-2">
                        <DocumentsNeededList documents={documentsNeeded} onDocumentClick={getDocument} customClass="pl-8" showBar={true}/>

                        <a
                            onClick={handleDifferentDocumentUpload}
                            className="inline-flex items-center justify-center w-full px-4 py-2 mt-4 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-500 dark:hover:bg-blue-600"
                        >
                            <Upload className="w-4 h-4 mr-2"/>
                            Upload a Document Not Listed Above
                        </a>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div className="page-container p-4 md:px-6 md:py-6 bg-gray-50 dark:bg-gray-900 min-h-screen z-10 relative">
            <div className="max-w-7xl mx-auto">
                <div className={`flex flex-row flex-wrap justify-between items-center mb-8`}>
                    <div className="mb-6">
                        {user.user_type === 'admin' && (
                            <Link to={`/clients/${client.id}`}><h2>Upload Documents: {client.profile.client}</h2></Link>
                        )}
                        <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
                            Upload
                            {currentDocument && (
                                <span className="text-gray-500 dark:text-gray-400 text-lg ml-2">
                                {currentDocument.name}: {currentDocument.description}
                            </span>
                            )}
                        </h3>
                        {user.user_type !== 'admin' && (
                            <CustomerAccountActions user={user} includeProgressBtn={true} year={taxYear} />
                        )}
                    </div>
                    <SelectTaxYear selectedYear={taxYear}
                                   includeText={true}
                                   availableYears={availableYears}/>
                    {user.user_type === 'admin' && (
                        <div className="flex flex-row w-full items-center space-x-2 mt-2">
                            <ClientActions clientId={client.id} year={taxYear}/>
                        </div>
                    )}

                </div>


                <div className="flex flex-col md:flex-row gap-6">
                    {documentsNeeded.length >= 0 && <DocumentMenu/>}

                    {showUploader && (
                        <div className="flex-1 bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                            <form onSubmit={handleSubmit}>
                                {errors.map((error, index) => (
                                    <div
                                        key={index}
                                        className="mb-4 p-4 text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900/30 rounded-md flex items-start justify-between"
                                    >
                                        <p>{error}</p>
                                        <button
                                            type="button"
                                            onClick={() => setErrors(errors.filter((_, i) => i !== index))}
                                            className="text-red-700 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                                        >
                                            <X className="w-4 h-4"/>
                                        </button>
                                    </div>
                                ))}

                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                                            {currentDocument && (<span style={{ lineHeight: 1 }}
                                                                       className={` text-gray-500 dark:text-gray-400 text-lg`}>Upload {currentDocument.name} <br/> <span className="text-sm">{currentDocument.description}</span></span>
                                            )}
                                            {!currentDocument && (
                                                <span>Upload Document</span>
                                            )}
                                        </label>
                                        <input
                                            ref={fileInputRef} // Add ref here
                                            type="file"
                                            onChange={handleFileChange}
                                            multiple
                                            required
                                            className="block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4
                                        file:rounded-md file:border-0 file:text-sm file:font-medium
                                        file:bg-blue-50 dark:file:bg-blue-900/30 file:text-blue-700 dark:file:text-blue-400
                                        hover:file:bg-blue-100 dark:hover:file:bg-blue-900/40"
                                        />
                                    </div>

                                    {files.map((file, index) => (
                                        <div key={index} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Change File Name (Please label file with descriptive name)
                                            </label>
                                            <input
                                                type="text"
                                                required
                                                placeholder={file.name}
                                                value={fileNames[index]}
                                                onChange={(e) => handleFileNameChange(index, e.target.value)}
                                                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600
                                            dark:bg-gray-800 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                            />
                                        </div>
                                    ))}

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Document Comments
                                        </label>
                                        <textarea
                                            value={comments}
                                            onChange={(e) => setComments(e.target.value)}
                                            placeholder="Add any comments about this document..."
                                            rows={4}
                                            className="mt-1 p-3 block w-full rounded-md border-gray-300 dark:border-gray-600
                                        dark:bg-gray-800 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                        />
                                    </div>

                                    {user.user_type === 'admin' && (
                                        <div className="flex items-center space-x-2">
                                            <input
                                                type="checkbox"
                                                id="locked"
                                                name="locked"
                                                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:ring-offset-gray-800"
                                            />
                                            <label
                                                htmlFor="locked"
                                                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                                            >
                                                Lock Document
                                            </label>
                                        </div>
                                    )}
                                    <button
                                        type="submit"
                                        disabled={isUploading}
                                        className={`
                                    focus:outline-none focus:ring-2 focus:ring-offset-2
                                    inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md
                                    shadow-sm
                                         px-6 py-2 rounded text-white
                                         ${isUploading ?
                                           'bg-blue-400 cursor-not-allowed animate-pulse' :
                                           'bg-blue-500 hover:bg-blue-600'
                                        }
                                         transition-colors duration-200
                                     `}
                                    >
                                        <Plus className="w-4 h-4 mr-2"/>
                                        {isUploading ? 'Uploading...' : 'Upload Document'}
                                    </button>
                                </div>
                            </form>
                        </div>

                    )}
                </div>
            </div>
        </div>
    );
};

export default UploadDocument;
